<template>
  <div class="p-4 lg:p-8">
    <div class="flex gap-2 mb-4">
      <Button v-for="filter in statusFilters" :key="filter.value" :label="filter.label" :class="selectedFilter === filter.value
        ? 'bg-purple-suit-forced border-none'
        : 'p-button-outlined p-button-secondary'" size="small" @click="applyFilter(filter.value)" />
    </div>

    <div class="surface-card shadow-sm border border-iron rounded-lg overflow-hidden">
      <DataTable :value="tickets" :loading="loading" dataKey="uuid" :paginator="true" :rows="10"
        responsiveLayout="stack" breakpoint="960px" class="p-datatable-sm">
        <template #empty>
          <div class="text-center py-8 text-gray-400">
            <i class="pi pi-inbox text-4xl mb-3 block"></i>
            {{ translate('no calling found', 'ucfirst') }}.
          </div>
        </template>

        <Column field="name" :header="translate('title', 'ucfirst')" />

        <Column :header="translate('motive', 'ucfirst')">
          <template #body="{ data }">
            {{ data.getReasonName() }}
          </template>
        </Column>

        <Column :header="translate('course', 'ucfirst')">
          <template #body="{ data }">
            {{ data.course_title || '-' }}
          </template>
        </Column>

        <Column header="Status" class="text-center">
          <template #body="{ data }">
            <Tag :value="data.getStatusLabel()" :severity="data.getStatusSeverity()" />
          </template>
        </Column>

        <Column :header="translate('open at', 'capitalize')">
          <template #body="{ data }">
            {{ data.getCreatedAtDate() }}
          </template>
        </Column>

        <Column :header="translate('actions', 'ucfirst')">
          <template #body="{ data }">
            <Button icon="pi pi-eye" class="p-button-text p-button-secondary" v-tooltip="translate('see details', 'capitalize')"
              @click="openTicket(data)" />
            <Button v-if="!data.isClosed()" icon="pi pi-times-circle" class="p-button-text p-button-danger"
              v-tooltip="translate('close calling', 'capitalize')" @click="confirmClose(data)" />
          </template>
        </Column>
      </DataTable>
    </div>

    <Dialog v-model:visible="ticketDialog" :style="{ width: '680px' }" :header="translate('open new calling', 'capitalize')" :modal="true"
      :closeOnEscape="true" class="p-fluid">
      <div class="flex flex-col gap-4 pt-2">

        <div class="field">
          <label class="font-semibold text-sm text-secondary mb-1 block">
            {{ translate('title of calling', 'ucfirst') }} *
          </label>
          <InputText v-model="form.name" :placeholder="translate('describe the problem', 'ucfirst')" class="w-full" />
        </div>

        <div class="field">
          <label class="font-semibold text-sm text-secondary mb-1 block">
            {{ translate('motive', 'ucfirst') }}
          </label>
          <Dropdown v-model="form.reason_id" :options="reasons" optionLabel="name" optionValue="id"
            placeholder="Selecione um motivo (opcional)" class="w-full" :loading="loadingReasons" />
        </div>

        <div class="field">
          <label class="font-semibold text-sm text-secondary mb-1 block">Curso relacionado</label>
          <Dropdown v-model="form.course_uuid" :options="myCourses" optionLabel="title" optionValue="uuid"
            placeholder="Selecione um curso (opcional)" class="w-full" :loading="loadingCourses" :filter="true"
            filterPlaceholder="Buscar curso..." />
        </div>

        <div class="field">
          <label class="font-semibold text-sm text-secondary mb-1 block">Mensagem *</label>
          <Editor v-model="form.message" editorStyle="height: 220px" />
        </div>

        <div class="flex justify-end gap-2 mt-2">
          <Button label="Cancelar" class="p-button-text p-button-secondary" @click="ticketDialog = false"
            :disabled="submitting" />
          <Button label="Enviar Chamado" icon="pi pi-send" class="bg-purple-suit-forced border-none"
            @click="submitTicket()" :loading="submitting" />
        </div>
      </div>
    </Dialog>

    <!-- Dialog: Ver Chamado -->
    <Dialog v-model:visible="viewDialog" :style="{ width: '760px' }"
      :header="selectedTicket ? selectedTicket.name : 'Chamado'" :modal="true" :closeOnEscape="true">
      <div v-if="selectedTicket" class="flex flex-col gap-4">

        <!-- Status + datas -->
        <div class="flex flex-wrap gap-2 items-center">
          <Tag :value="translate(selectedTicket.getStatusLabel(), 'ucfirst')"
            :severity="selectedTicket.getStatusSeverity()" />
          <span class="text-xs text-gray-400">Aberto em {{ selectedTicket.getCreatedAtDate() }}</span>
          <span v-if="selectedTicket.isClosed()" class="text-xs text-gray-400">
            · Fechado em {{ selectedTicket.getClosedAtDate() }}
          </span>
        </div>

        <!-- Motivo + Curso -->
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p class="text-gray-400 mb-1 font-bold">Motivo</p>
            <p class="font-medium">{{ selectedTicket.getReasonName() }}</p>
          </div>
          <div>
            <p class="text-gray-400 mb-1 font-bold">Curso</p>
            <p class="font-medium">{{ selectedTicket.course_title || '-' }}</p>
          </div>
        </div>

        <Divider />

        <!-- Mensagem original -->
        <div>
          <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Mensagem inicial</p>
          <div class="prose max-w-none text-sm bg-surface-50 border border-iron rounded-lg p-3"
            v-html="selectedTicket.message" />
        </div>

        <!-- Thread de Respostas -->
        <div>
          <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
            Respostas
            <span v-if="replies.length" class="ml-1 text-purple-suit">({{ replies.length }})</span>
          </p>

          <!-- Loading replies -->
          <div v-if="loadingReplies" class="flex justify-center py-6">
            <i class="pi pi-spin pi-spinner text-2xl text-gray-400"></i>
          </div>

          <!-- Lista de replies -->
          <div v-else-if="replies.length" class="flex flex-col gap-3 max-h-72 overflow-y-auto pr-1"
            ref="repliesContainer">
            <div v-for="reply in replies" :key="reply.uuid" class="flex gap-3"
              :class="reply.user == authStore.getUserId ? 'flex-row-reverse' : 'flex-row'">
              <!-- Avatar -->
              <div class="flex-shrink-0">
                <div class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  :class="reply.is_admin ? 'bg-carrot' : 'bg-blog-red'" :title="reply.user_name">
                  {{ getInitials(reply.user_name) }}
                </div>
              </div>

              <!-- Balão -->
              <div class="flex flex-col max-w-[80%]">
                <div class="flex items-center gap-2 mb-1">
                  <span class="text-xs font-semibold text-secondary">{{ reply.user_name }}</span>
                  <span v-if="reply.is_admin" class="text-xs bg-purple-100 text-purple-700 rounded px-1">Admin</span>
                  <span class="text-xs text-gray-400">{{ formatReplyDate(reply.created_at) }}</span>
                </div>
                <div
                  class="prose prose-sm max-w-none text-sm rounded-lg px-3 py-2 border w-full break-words whitespace-pre-line"
                  :class="reply.is_admin
                    ? 'bg-purple-50 border-purple-200 text-left'
                    : 'bg-gray-50 border-gray-200 text-left'" v-html="reply.message" />
              </div>
            </div>
          </div>

          <!-- Sem respostas -->
          <div v-else class="text-center py-4 text-gray-400 text-sm">
            <i class="pi pi-comments text-2xl mb-2 block"></i>
            Nenhuma resposta ainda.
          </div>
        </div>

        <!-- Formulário de nova resposta (apenas se não fechado) -->
        <div v-if="!selectedTicket.isClosed()">
          <Divider />
          <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Adicionar resposta</p>
          <Editor v-model="replyMessage" editorStyle="height: 140px" />
          <div class="flex justify-end mt-2">
            <Button label="Enviar Resposta" icon="pi pi-send" class="bg-purple-suit-forced border-none"
              @click="submitReply()" :loading="submittingReply" />
          </div>
        </div>

        <Divider />

        <div class="flex justify-end gap-2 mt-2">
          <Button v-if="!selectedTicket.isClosed()" label="Fechar Chamado" icon="pi pi-times-circle"
            class="p-button-outlined p-button-danger" @click="confirmClose(selectedTicket)" />
          <Button label="Fechar" class="p-button-text p-button-secondary" @click="viewDialog = false" />
        </div>
      </div>
    </Dialog>

    <ConfirmDialog />
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch, onUnmounted } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import Editor from 'primevue/editor';
import ConfirmDialog from 'primevue/confirmdialog';
import { Divider } from 'primevue';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { showToast } from '@/composables/toast';
import SupportService from '@/services/SupportService';
import SupportTicketDictionary from '@/dictionary/SupportTicketDictionary';
import { translate } from '@/composables/translate';
import { useAuthStore } from '@/stores/auth';

const toast = useToast();
const confirm = useConfirm();
const authStore = useAuthStore();

const supportTicketDictionary = new SupportTicketDictionary();
const statusFilters = supportTicketDictionary.getAll(['']);

// --- State ---
const tickets = ref([]);
const loading = ref(false);
const reasons = ref([]);
const loadingReasons = ref(false);
const myCourses = ref([]);
const loadingCourses = ref(false);
const submitting = ref(false);
const selectedFilter = ref(supportTicketDictionary.OPEN);
const ticketDialog = ref(false);
const viewDialog = ref(false);
const selectedTicket = ref(null);

// Replies
const replies = ref([]);
const loadingReplies = ref(false);
const replyMessage = ref('');
const submittingReply = ref(false);
const repliesContainer = ref(null);

const form = ref({
  name: '',
  reason_id: null,
  course_uuid: null,
  message: ''
});

// --- Lifecycle ---
onMounted(() => {
  loadTickets();
});

watch(viewDialog, (isOpen) => {
  if (isOpen) {
    startPolling();
  } else {
    stopPolling();
  }
});

onUnmounted(() => {
  stopPolling();
});

// --- Methods ---
async function loadTickets() {
  loading.value = true;
  const filters = selectedFilter.value ? { status: selectedFilter.value } : {};
  tickets.value = await SupportService.get(filters);
  loading.value = false;
}

async function submitTicket() {
  if (!form.value.name.trim()) {
    showToast(toast, 'O título do chamado é obrigatório.', 'error');
    return;
  }
  if (!form.value.message || form.value.message === '<p><br></p>') {
    showToast(toast, 'A mensagem é obrigatória.', 'error');
    return;
  }

  submitting.value = true;
  const response = await SupportService.store(form.value);
  submitting.value = false;

  if (!response.success()) {
    showToast(toast, response.getMessage() || 'Erro ao abrir chamado.', 'error');
    return;
  }

  showToast(toast, 'Chamado aberto com sucesso!', 'success');
  ticketDialog.value = false;
  loadTickets();
}

async function openTicket(ticket) {
  selectedTicket.value = ticket;
  replies.value = [];
  viewDialog.value = true;
  await loadReplies(ticket.uuid);
}

async function loadReplies(uuid) {
  loadingReplies.value = true;
  replies.value = await SupportService.getReplies(uuid);
  loadingReplies.value = false;
  scrollRepliesToBottom();
}

// --- Métodos de Polling e Checagem de Novas Replies ---
const pollingInterval = ref(null);
function startPolling() {
  stopPolling();

  // Define a execução a cada 10 segundos (10000ms)
  pollingInterval.value = setInterval(async () => {
    if (!selectedTicket.value || !viewDialog.value) {
      stopPolling();
      return;
    }

    const currentReplyIds = replies.value.map(reply => reply.uuid);
    try {
      const hasNewReplies = await SupportService.checkNewReplies(selectedTicket.value.uuid, currentReplyIds);

      if (hasNewReplies) {
        await fetchNewRepliesSilently(selectedTicket.value.uuid);
      }
    } catch (error) {
      console.error("Erro ao validar novas respostas:", error);
    }
  }, 10000);
}

function stopPolling() {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
}

// Uma versão "silenciosa" do loadReplies para atualizar o chat sem travar a tela com um spinner no meio da conversa
async function fetchNewRepliesSilently(uuid) {
  replies.value = await SupportService.getReplies(uuid);
  scrollRepliesToBottom();
}

async function submitReply() {
  if (!replyMessage.value || replyMessage.value === '<p><br></p>') {
    showToast(toast, 'A mensagem de resposta é obrigatória.', 'error');
    return;
  }

  submittingReply.value = true;
  const response = await SupportService.reply(selectedTicket.value.uuid, { message: replyMessage.value });
  submittingReply.value = false;

  if (!response.success()) {
    showToast(toast, response.getMessage() || 'Erro ao enviar resposta.', 'error');
    return;
  }

  replyMessage.value = '';
  await loadReplies(selectedTicket.value.uuid);
}

function applyFilter(value) {
  selectedFilter.value = value;
  loadTickets();
}

function confirmClose(ticket) {
  confirm.require({
    message: 'Tem certeza que deseja fechar este chamado?',
    header: 'Fechar Chamado',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Sim, fechar',
    rejectLabel: 'Cancelar',
    acceptClass: 'p-button-danger',
    accept: async () => {
      const response = await SupportService.close(ticket.uuid);
      if (!response.success()) {
        showToast(toast, response.getMessage() || 'Erro ao fechar chamado.', 'error');
        return;
      }
      showToast(toast, 'Chamado fechado com sucesso.', 'success');
      viewDialog.value = false;
      loadTickets();
    }
  });
}

function scrollRepliesToBottom() {
  nextTick(() => {
    if (repliesContainer.value) {
      repliesContainer.value.scrollTop = repliesContainer.value.scrollHeight;
    }
  });
}

function formatReplyDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function getInitials(name = '') {
  return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
}
</script>